<template>
  <div class="feedback-page">
    <h1 class="feedback-title">Share your feedback</h1>
    <div class="feedback-container">
      <nav class="feedback-nav mt-5">
        <button 
          :class="{ active: isSubmitFeedbackVisible }" 
          @click="showSubmitFeedback">Submit a Feedback
        </button>
        <button 
          :class="{ active: !isSubmitFeedbackVisible }" 
          @click="showMyFeedback">My Feedbacks
        </button>
      </nav>

      <!-- Feedback Submission Form -->
      <FeedbackForm 
        v-if="isSubmitFeedbackVisible" 
        :loading="loading"
        :initial-feedback="currentFeedbackText" 
        @submit-feedback="handleFeedbackSubmission"
        ref="feedbackFormRef"
      />

      <!-- Feedback Table -->
      <FeedbackList 
        v-else 
        :feedback-list="feedbackList"
      />

    </div>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar.show" :timeout="3000" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "@/utils/firebase.utils"; // Ensure you have Firebase initialized
import { httpsCallable } from "firebase/functions";
import { functions } from "@/utils/firebase.utils";

import FeedbackForm from './FeedbackForm.vue';
import FeedbackList from './FeedbackList.vue';

export default {
  components: {
    FeedbackForm,
    FeedbackList
  },
  data() {
    return {
      isSubmitFeedbackVisible: true,
      currentFeedbackText: '', // Used to hold feedback text for the form
      feedbackList: [],
      loading: false,
      snackbar: {
        show: false,
        message: '',
        color: ''
      },
    };
  },
  computed: {
    currentUID() {
      return auth.currentUser ? auth.currentUser.uid : null;
    }
  },
  methods: {
    showSubmitFeedback() {
      this.isSubmitFeedbackVisible = true;
    },
    showMyFeedback() {
      this.isSubmitFeedbackVisible = false;
      if (this.currentUID) {
        this.fetchFeedback();
      } else {
        console.warn("User not authenticated, cannot fetch feedback.");
        this.feedbackList = []; // Clear list if user is not logged in
      }
    },
    async fetchFeedback() {
      if (!this.currentUID) {
        console.warn("Attempted to fetch feedback without a user ID.");
        this.feedbackList = [];
        return;
      }
      try {
        const feedbackRef = collection(db, "users", this.currentUID, "Feedback");
        const querySnapshot = await getDocs(feedbackRef);

        if (querySnapshot.empty) {
          this.feedbackList = [];
          return;
        }

        this.feedbackList = querySnapshot.docs.map(doc => {
          let data = doc.data();
          return {
            id: doc.id,
            created_at: data.created_at?.seconds
              ? new Date(data.created_at.seconds * 1000 + data.created_at.nanoseconds / 1000000)
              : new Date(),
            text: data.text || "No feedback text"
          };
        }).sort((a, b) => b.created_at - a.created_at); // Sort by newest first

      } catch (error) {
        console.error("Error fetching feedback:", error);
        this.snackbar = {
          show: true,
          message: "Error fetching your feedback. Please try again.",
          color: "red"
        };
      }
    },
    async handleFeedbackSubmission(feedbackText) {
      if (!this.currentUID) {
        this.snackbar = {
          show: true,
          message: "You must be logged in to submit feedback.",
          color: "red"
        };
        return;
      }
      
      this.loading = true;
      this.currentFeedbackText = feedbackText; // Keep current text in case of error

      try {
        const sendFeedback = httpsCallable(functions, "sendFeedBackEmailToTeam");
        const response = await sendFeedback({ uid: this.currentUID, text: feedbackText });

        if (response.data.success) {
          this.snackbar = {
            show: true,
            message: "Thank you! Your feedback has been received.",
            color: "green"
          };
          this.currentFeedbackText = ''; // Clear text after successful submission
          if (this.$refs.feedbackFormRef) {
             this.$refs.feedbackFormRef.clearInput(); // Clear form input
          }
          if (!this.isSubmitFeedbackVisible) { // If on 'My Feedbacks' tab, refresh list
            this.fetchFeedback();
          } else {
            // If on submit tab, and we want to show the new feedback immediately,
            // we might switch tabs or just update the list if it's potentially visible elsewhere
            // For now, we just fetch it if the other tab is active.
          }
        } else {
          throw new Error(response.data.message || "Failed to submit feedback.");
        }
      } catch (error) {
        console.error("Error submitting feedback:", error);
        this.snackbar = {
          show: true,
          message: error.message || "Sorry, we couldn't receive your feedback.",
          color: "red"
        };
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Listen for auth state changes to fetch UID
    auth.onAuthStateChanged(user => {
      if (user) {
        // User is signed in, you can get UID here if needed immediately
        // or rely on computed property currentUID
        if (!this.isSubmitFeedbackVisible) { // If 'My Feedbacks' is active tab
            this.fetchFeedback(); 
        }
      } else {
        // User is signed out.
        this.feedbackList = []; // Clear feedback if user signs out
        console.warn("User signed out. Feedback list cleared.");
      }
    });
    // Initial fetch if user is already logged in and on 'My Feedbacks' tab
    if (this.currentUID && !this.isSubmitFeedbackVisible) {
        this.fetchFeedback();
    }
  }
};
</script>

<style scoped>
.feedback-page {
  padding: 20px;
  max-width: 840px;
  margin: auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; /* Modern font */
}
.feedback-title {
  font-size: 28px; /* Slightly larger title */
  font-weight: 600; /* Semi-bold */
  text-align: center;
  margin-bottom: 30px; /* Increased margin */
  color: #333; /* Darker text color */
}
.feedback-nav {
  display: flex;
  justify-content: center; /* Center navigation tabs */
  gap: 10px; /* Increased gap */
  margin-bottom: 25px; /* Increased margin */
}
.feedback-nav button {
  min-width: 160px; /* Slightly wider buttons */
  padding: 12px 20px; /* Increased padding for better clickability */
  border: 1px solid transparent; /* Start with transparent border */
  border-radius: 25px; /* Pill-shaped buttons */
  cursor: pointer;
  text-align: center;
  font-weight: 500;
  transition: all 0.3s ease; /* Smooth transition for hover/active states */
}
.feedback-nav button.active {
  background-color: #007bff; /* Primary color */
  color: white;
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2); /* Subtle shadow for active tab */
}
.feedback-nav button:not(.active) {
  background-color: #f0f0f0; /* Lighter background for inactive tabs */
  color: #333;
  border-color: #ddd; /* Light border for inactive tabs */
}
.feedback-nav button:not(.active):hover {
  background-color: #e9e9e9; /* Slightly darker on hover for inactive */
  border-color: #ccc;
}
.feedback-container {
  border: 1px solid #e0e0e0; /* Lighter border */
  border-radius: 12px; /* More rounded corners */
  padding: 25px; /* Increased padding */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Softer, more modern shadow */
  background-color: white;
}

/* Responsive Design */
@media (max-width: 768px) { /* Adjusted breakpoint */
  .feedback-nav {
    flex-direction: column;
    align-items: stretch; /* Make buttons full width in column */
  }
  .feedback-nav button {
    width: 100%;
    margin-bottom: 8px; /* Add some space between stacked buttons */
  }
  .feedback-nav button:last-child {
    margin-bottom: 0;
  }
}

@media (max-width: 600px) {
  .feedback-container {
    padding: 20px;
  }
  .feedback-title {
    font-size: 24px;
  }
  /* Further adjustments for smaller screens if needed */
}
</style>
